import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatErr } from "../../../../../lib/api";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../../../../../components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../../../../components/ui/select";
import { toast } from "sonner";
import { SectionCard, EmptyState } from "../shared";
import SearchableSelect from "../SearchableSelect";
import { ACCOUNTING_BASE } from "../accountingNav";
import { ReportFilterBar, ReportPrintShell, useFinancialYears, downloadReportCsv, formatCurrency, formatDateDMY } from "./reportShared";

const VOUCHER_TYPES = ["RECEIPT", "PAYMENT", "CONTRA", "JOURNAL", "PURCHASE", "CREDIT_NOTE", "DEBIT_NOTE", "ADJUSTMENT", "OPENING"];

/**
 * Phase 8 - Day Book (item 9). Chronological posted vouchers with
 * date/type/ledger/party/amount/search filters. Cancelled vouchers are only
 * shown when explicitly asked for (status=all) and are always excluded from
 * the footer totals (item 42), tagged clearly in the Status column.
 */
export default function DayBookPage() {
  const navigate = useNavigate();
  const financialYears = useFinancialYears();
  const [ledgers, setLedgers] = useState([]);
  const [financialYearId, setFinancialYearId] = useState("");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [voucherType, setVoucherType] = useState("__all__");
  const [ledgerId, setLedgerId] = useState("");
  const [status, setStatus] = useState("posted");
  const [search, setSearch] = useState("");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const res = await api.get("/society/accounting/engine/ledgers");
        setLedgers(Array.isArray(res.data) ? res.data : []);
      } catch { setLedgers([]); }
    })();
  }, []);
  const ledgerOptions = useMemo(() => [{ value: "", label: "All ledgers" }, ...ledgers.map((l) => ({ value: l.id, label: l.name }))], [ledgers]);

  const params = { financialYearId, fromDate, toDate, voucherType: voucherType === "__all__" ? "" : voucherType, ledgerId, status, search };

  const load = async () => {
    try {
      setLoading(true);
      const res = await api.get("/society/accounting/reports/day-book", { params });
      setData(res.data);
    } catch (err) {
      toast.error(formatErr(err, "Could not load day book"));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [financialYearId, fromDate, toDate, voucherType, ledgerId, status, search]);

  const rows = data?.rows || [];

  return (
    <div className="space-y-5">
      <div>
        <h2 className="text-lg font-extrabold text-[#111827]">Day Book</h2>
        <p className="mt-1 text-sm text-[#6B7280]">Every posted voucher, in date order.</p>
      </div>

      <div className="report-screen-only space-y-5">
        <ReportFilterBar
          financialYears={financialYears} financialYearId={financialYearId} onFinancialYearChange={setFinancialYearId}
          fromDate={fromDate} toDate={toDate} onFromDateChange={setFromDate} onToDateChange={setToDate}
          search={search} onSearchChange={setSearch} searchPlaceholder="Narration, voucher no., reference"
          extra={
            <>
              <div className="w-40 space-y-1">
                <div className="text-[11px] font-semibold uppercase tracking-[0.14em] text-[#6B7280]">Voucher Type</div>
                <Select value={voucherType} onValueChange={setVoucherType}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__all__">All types</SelectItem>
                    {VOUCHER_TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="w-52 space-y-1">
                <div className="text-[11px] font-semibold uppercase tracking-[0.14em] text-[#6B7280]">Ledger</div>
                <SearchableSelect value={ledgerId} onChange={setLedgerId} options={ledgerOptions} placeholder="All ledgers" />
              </div>
              <div className="w-36 space-y-1">
                <div className="text-[11px] font-semibold uppercase tracking-[0.14em] text-[#6B7280]">Status</div>
                <Select value={status} onValueChange={setStatus}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="posted">Posted only</SelectItem>
                    <SelectItem value="all">Include cancelled</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </>
          }
          onExport={() => downloadReportCsv("/society/accounting/reports/day-book", params, `day-book-${data?.fromDate || ""}-to-${data?.toDate || ""}.csv`)}
          onPrint={() => window.print()}
        />

        {loading ? (
          <div className="text-sm text-[#6B7280]">Loading...</div>
        ) : rows.length === 0 ? (
          <EmptyState>No vouchers in this range.</EmptyState>
        ) : (
          <SectionCard>
            <div className="overflow-x-auto rounded-sm border border-[#E5E7EB]">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Date</TableHead><TableHead>Voucher No.</TableHead><TableHead>Type</TableHead>
                    <TableHead>Party</TableHead><TableHead>Narration</TableHead><TableHead>Reference</TableHead>
                    <TableHead className="text-right">Debit Total</TableHead><TableHead className="text-right">Credit Total</TableHead><TableHead>Status</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {rows.map((row) => (
                    <TableRow key={row.voucherId} className={row.status !== "posted" ? "opacity-60" : ""}>
                      <TableCell>{formatDateDMY(row.voucherDate)}</TableCell>
                      <TableCell>
                        <button type="button" className="font-semibold text-[#111827] hover:underline" onClick={() => navigate(`${ACCOUNTING_BASE}/vouchers/${row.voucherId}`)}>{row.voucherNumber || "(draft)"}</button>
                      </TableCell>
                      <TableCell>{row.voucherType}</TableCell>
                      <TableCell>{row.partyName || "-"}</TableCell>
                      <TableCell>{row.narration}</TableCell>
                      <TableCell>{row.referenceNo || "-"}</TableCell>
                      <TableCell className="text-right">{formatCurrency(row.debitTotal)}</TableCell>
                      <TableCell className="text-right">{formatCurrency(row.creditTotal)}</TableCell>
                      <TableCell>
                        {row.status === "posted" ? <span className="rounded-sm bg-emerald-50 px-2 py-0.5 text-xs font-semibold text-emerald-700">Posted</span> : <span className="rounded-sm bg-red-50 px-2 py-0.5 text-xs font-semibold text-red-700">{row.status}</span>}
                      </TableCell>
                    </TableRow>
                  ))}
                  <TableRow className="border-t-2 border-[#111827] font-bold">
                    <TableCell colSpan={6}>Total (posted only)</TableCell>
                    <TableCell className="text-right">{formatCurrency(data.totalDebit)}</TableCell>
                    <TableCell className="text-right">{formatCurrency(data.totalCredit)}</TableCell>
                    <TableCell />
                  </TableRow>
                </TableBody>
              </Table>
            </div>
          </SectionCard>
        )}
      </div>

      {data && rows.length > 0 && (
        <div className="report-print-only">
          <ReportPrintShell reportName="Day Book" rangeLabel={`${data.financialYear ? `FY ${data.financialYear} — ` : ""}${data.fromDate} to ${data.toDate}`}>
            <table className="w-full border-collapse text-[10px]">
              <thead><tr className="border-b border-black"><th className="py-1 text-left">Date</th><th className="py-1 text-left">Voucher</th><th className="py-1 text-left">Narration</th><th className="py-1 text-right">Debit</th><th className="py-1 text-right">Credit</th></tr></thead>
              <tbody>
                {rows.map((row) => (
                  <tr key={row.voucherId} className="border-b border-gray-300">
                    <td className="py-0.5">{formatDateDMY(row.voucherDate)}</td>
                    <td className="py-0.5">{row.voucherNumber}{row.status !== "posted" ? ` (${row.status})` : ""}</td>
                    <td className="py-0.5">{row.narration}</td>
                    <td className="py-0.5 text-right">{formatCurrency(row.debitTotal)}</td>
                    <td className="py-0.5 text-right">{formatCurrency(row.creditTotal)}</td>
                  </tr>
                ))}
                <tr className="border-t-2 border-black font-bold"><td colSpan={3} className="py-1">Total (posted only)</td><td className="py-1 text-right">{formatCurrency(data.totalDebit)}</td><td className="py-1 text-right">{formatCurrency(data.totalCredit)}</td></tr>
              </tbody>
            </table>
          </ReportPrintShell>
        </div>
      )}
    </div>
  );
}
